import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import { PrimaryPassengerTravelPreferences } from '@/app/(main)/clients/create/components/PrimaryPassengerTravelPreferences';
import { SectionAccordion } from '@/app/(main)/clients/create/components/SectionAccordion';
import { ConfirmationModalWrapper } from '@/app/components/ConfirmationModalWrapper';
import { useUser } from '@/context/UserProvider';
import type { ClientDocumentResponse, CreateClientFormData, PassportDataWithName } from '@/types/clientsApiTypes';
import { useCreateRelatedPassenger } from '../../hooks/useCreateRelatedPassenger';
import { useDeleteClientDocument } from '../../hooks/useDeleteClientDocument';
import { useDeleteLoyaltyProgramMutation } from '../../hooks/useDeleteLoyaltyProgramMutation';
import { useDeleteRelatedPassenger } from '../../hooks/useDeleteRelatedPassenger';
import { useGetConfirmationModalContent } from '../../hooks/useGetConfirmationModalContent';
import { useGetIsPassengerValid } from '../../hooks/useGetIsPassengerValid';
import { useUpdateClientByFieldName } from '../../hooks/useUpdateClientByFieldName';
import { useUpdateClientDocumentMutation } from '../../hooks/useUpdateClientDocumentMutation';
import { useUpdateTravelPreferencesByFieldName } from '../../hooks/useUpdateTravelPreferencesByFieldName';
import { useUpdateTravelPreferencesWithRelatedData } from '../../hooks/useUpdateTravelPreferencesWithRelatedData';
import { useUploadImageToClient } from '../../hooks/useUploadImageToClient';
import { getDocumentPayload } from '../../utils/getDocumentPayload';
import { getPassengerTitle } from '../../utils/getPassengerTitle';
import { REQUIRED_FORM_KEYS } from '../../utils/validationSchema';
import { ContactDetailsSection } from '../CreateClientContactDetailsSection';
import { MailingAddressSection } from '../CreateClientMailingAddressSection';
import { MainInfoSection } from '../CreateClientMainInfoSection';
import { RelatedPassengers } from '../CreateClientRelatedPassengers';
import { DocumentsSection } from '../DocumentsSection';
import { PassengerItemWrapper } from '../PassengerItemWrapper';
import { Section } from '../Section';
import { UploadImageSection } from '../UploadImageSection';

interface CreateClientFormProps {
  isPublicPage?: boolean;
  setIsUnsavedChanges?: (value: boolean) => void;
}

export const CreateClientForm = ({ isPublicPage, setIsUnsavedChanges }: CreateClientFormProps) => {
  const {
    watch,
    formState: { isDirty },
    setValue,
  } = useFormContext<CreateClientFormData>();
  const getConfirmationModalContent = useGetConfirmationModalContent<CreateClientFormData>(
    isDirty,
    watch,
    REQUIRED_FORM_KEYS
  );
  const { mutateAsync: createRelatedPassengerMutation } = useCreateRelatedPassenger();
  const { mutate: deleteRelatedPassengerMutation } = useDeleteRelatedPassenger();
  const { mutateAsync: deleteLoyaltyProgramMutation } = useDeleteLoyaltyProgramMutation();
  const { onUploadImage, onDeleteImage, onUploadImageFromGallery, isLoading, isDeleteLoading } =
    useUploadImageToClient();
  const { updateClientByFieldName } = useUpdateClientByFieldName();
  const { mutate: updateClientDocumentMutation } = useUpdateClientDocumentMutation();
  const { updateTravelPreferencesWithRelatedData } = useUpdateTravelPreferencesWithRelatedData();
  const { updateTravelPreferencesByFieldName } = useUpdateTravelPreferencesByFieldName();
  const { mutate: deleteClientDocument } = useDeleteClientDocument();

  const { user } = useUser();
  const userId = user?.id ?? null;

  const primaryPassengerDocuments = watch('primary_passenger.documents');
  const primaryPassengerId = watch('primary_passenger.client.client_id');
  const firstName = watch('primary_passenger.client.first_name');
  const lastName = watch('primary_passenger.client.last_name');
  const { isPrimaryPassengerValid, isRelatedPassengersValid, arePrimaryNamesWithinLimit, areRelatedNamesWithinLimit } =
    useGetIsPassengerValid();

  const primaryPassengerHeader = getPassengerTitle(firstName, lastName, true);

  const createRelatedPassenger = useCallback(
    async (primaryPassengerId: number) => {
      return createRelatedPassengerMutation(primaryPassengerId, {
        onSuccess: () => {
          setIsUnsavedChanges?.(false);
        },
      });
    },
    [createRelatedPassengerMutation, setIsUnsavedChanges]
  );

  const deleteRelatedPassenger = useCallback(
    (passengerId: number) => {
      deleteRelatedPassengerMutation(passengerId, {
        onSuccess: () => {
          toast.success('Related passenger deleted successfully');
          setIsUnsavedChanges?.(false);
        },
      });
    },
    [deleteRelatedPassengerMutation, setIsUnsavedChanges]
  );

  const onUpdateFormData = useCallback(
    async (fieldName: string, fieldValue: unknown) => {
      return updateClientByFieldName(fieldName, fieldValue, () => {
        setIsUnsavedChanges?.(false);
      });
    },
    [updateClientByFieldName, setIsUnsavedChanges]
  );

  const onUpdateTravelPreferences = useCallback(
    async (fieldName: string, fieldValue: unknown) => {
      return updateTravelPreferencesByFieldName(fieldName, fieldValue, () => {
        setIsUnsavedChanges?.(false);
      });
    },
    [updateTravelPreferencesByFieldName, setIsUnsavedChanges]
  );

  const handleUpdateTravelPreferencesWithRelatedData = useCallback(
    async (fieldKey: any, fieldName: string, fieldValue: unknown, fullPath: string) => {
      return updateTravelPreferencesWithRelatedData(fieldKey, fieldName, fieldValue, fullPath, () => {
        setIsUnsavedChanges?.(false);
      });
    },
    [updateTravelPreferencesWithRelatedData, setIsUnsavedChanges]
  );

  const onDeleteLoyaltyProgram = useCallback(
    async (passengerId: number, lpId: number) => {
      if (!passengerId) {
        throw new Error('Client ID is required');
      }
      await deleteLoyaltyProgramMutation(
        { clientId: passengerId, lpId },
        {
          onSuccess: () => {
            setIsUnsavedChanges?.(false);
          },
        }
      );
    },
    [deleteLoyaltyProgramMutation, setIsUnsavedChanges]
  );

  const onUpdateDocuments = useCallback(
    (documents: PassportDataWithName, documentId: number | null) => {
      if (!documentId) {
        return;
      }
      updateClientDocumentMutation(
        { data: getDocumentPayload(documents), documentId },
        {
          onSuccess: () => {
            const documents = watch('primary_passenger.documents');
            const newDocuments = documents.map((document) =>
              document.id === documentId ? { ...document, ...documents } : document
            );
            setValue('primary_passenger.documents', newDocuments);
            setIsUnsavedChanges?.(false);
          },
        }
      );
    },
    [updateClientDocumentMutation, setIsUnsavedChanges]
  );

  const onDeleteDocuments = useCallback(
    (documentId: number) => {
      deleteClientDocument(documentId, {
        onSuccess: () => {
          const documents = watch('primary_passenger.documents');
          const newDocuments = documents.filter((document) => document.id !== documentId);
          setValue('primary_passenger.documents', newDocuments);
          toast.success('Document deleted successfully');
          setIsUnsavedChanges?.(false);
        },
      });
    },
    [deleteClientDocument, setIsUnsavedChanges]
  );

  const handleDeleteImage = useCallback(() => {
    onDeleteImage?.(primaryPassengerId, () => {
      setIsUnsavedChanges?.(false);
    });
  }, [onDeleteImage, primaryPassengerId, setIsUnsavedChanges]);

  const handleUploadImage = useCallback(
    async (file: File) => {
      return onUploadImage?.(file, () => {
        setIsUnsavedChanges?.(false);
      });
    },
    [onUploadImage, setIsUnsavedChanges]
  );

  const handleUploadImageFromGallery = useCallback(
    async (imageId: number) => {
      return onUploadImageFromGallery?.(imageId, () => {
        setIsUnsavedChanges?.(false);
      });
    },
    [onUploadImageFromGallery, setIsUnsavedChanges]
  );

  const onSuccessUploadDocuments = useCallback(
    (data: ClientDocumentResponse) => {
      const documents = watch('primary_passenger.documents');
      const newDocuments = [...documents, data];
      setValue('primary_passenger.documents', newDocuments);
    },
    [watch, setValue]
  );

  const onSuccessUploadDocumentsRelatedPassengers = useCallback(
    (data: ClientDocumentResponse, index: number) => {
      const documents = watch(`related_passengers.${index}.documents`);
      const newDocuments = [...documents, data];
      setValue(`related_passengers.${index}.documents`, newDocuments, { shouldValidate: true });
    },
    [watch, setValue]
  );

  return (
    <ConfirmationModalWrapper shouldBlock={isDirty} modalProps={getConfirmationModalContent}>
      <form className="flex flex-col gap-[24px] mt-[6px]">
        <PassengerItemWrapper header={primaryPassengerHeader} isValid={isPrimaryPassengerValid && arePrimaryNamesWithinLimit}>
          <Section className="pb-0" isLast>
            <UploadImageSection
              onUploadImage={handleUploadImage}
              onDeleteImage={handleDeleteImage}
              onUploadImageFromGallery={handleUploadImageFromGallery}
              isLoading={isLoading}
              isDeleteLoading={isDeleteLoading}
              userId={userId}
            />
          </Section>

          <Section>
            <MainInfoSection onUpdateFormData={onUpdateFormData} />
          </Section>

          <Section
            title="Contact Details"
            description="You can add multiple email addresses and phone numbers. At least one email is required."
          >
            <ContactDetailsSection onUpdateFormData={onUpdateFormData} />
          </Section>

          <Section title="Mailing Address">
            <MailingAddressSection onUpdateFormData={onUpdateFormData} />
          </Section>

          <div className="flex flex-col py-[30px] px-6 border-b border-blue-100">
            <DocumentsSection
              isPublicPage={isPublicPage}
              passengerFullName={primaryPassengerHeader}
              onSuccess={onSuccessUploadDocuments}
              fieldPrefix="primary_passenger"
              documents={primaryPassengerDocuments || []}
              passengerId={primaryPassengerId}
              onUpdateDocuments={onUpdateDocuments}
              onDeleteDocuments={onDeleteDocuments}
            />
          </div>

          <Section title="" description="">
            <SectionAccordion sectionClassName="px-0 pt-4" header="Travel Profile & Preferences" arrowPosition="left">
              <PrimaryPassengerTravelPreferences
                onUpdateTravelPreferencesWithRelatedData={handleUpdateTravelPreferencesWithRelatedData}
                onUpdateFormData={onUpdateFormData}
                onUpdateTravelPreferences={onUpdateTravelPreferences}
                onDeleteLoyaltyProgram={(lpId) => onDeleteLoyaltyProgram(primaryPassengerId, lpId)}
              />
            </SectionAccordion>
          </Section>
        </PassengerItemWrapper>

        <PassengerItemWrapper index={2} header="Related Passengers" isValid={isRelatedPassengersValid && areRelatedNamesWithinLimit}>
          <Section
            description={
              isPrimaryPassengerValid
                ? 'Add any related passengers, if applicable. This can include family members or anyone you typically travel with as part of a group.'
                : 'Complete all required primary passenger details to add related passengers.'
            }
          >
            <RelatedPassengers
              onUpdateTravelPreferences={onUpdateTravelPreferences}
              onUpdateTravelPreferencesWithRelatedData={handleUpdateTravelPreferencesWithRelatedData}
              isPrimaryPassengerValid={isPrimaryPassengerValid}
              onUpdateFormData={onUpdateFormData}
              onAddPassenger={createRelatedPassenger}
              onDeletePassenger={deleteRelatedPassenger}
              onSuccessUploadDocuments={onSuccessUploadDocumentsRelatedPassengers}
              primaryPassengerId={primaryPassengerId}
              onUpdateDocuments={onUpdateDocuments}
              onDeleteDocuments={onDeleteDocuments}
              isPublicPage={isPublicPage}
              onDeleteLoyaltyProgram={(passengerId, lpId) => onDeleteLoyaltyProgram(passengerId, lpId)}
            />
          </Section>
        </PassengerItemWrapper>
      </form>
    </ConfirmationModalWrapper>
  );
};